import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const ecosystemData = [
  // Products (Inner Oval Ring - Distances increased by 30%)
  { 
    id: 'pilot', 
    label: 'Pilot', 
    desc: 'The flight deck for your event. Manage schedules, tickets, and attendees from one command center.', 
    icon: FiIcons.FiNavigation, 
    color: 'indigo', 
    radiusX: 364, // 280 * 1.3
    radiusY: 208, // 160 * 1.3
    angle: 0, 
    type: 'product' 
  },
  { 
    id: 'unity', 
    label: 'Unity', 
    desc: 'The visibility engine. Embed tickets, create promo links, and broadcast your event across the web.', 
    icon: FiIcons.FiGift, 
    color: 'emerald', 
    radiusX: 364, 
    radiusY: 208, 
    angle: 51.4, 
    type: 'product' 
  },
  { 
    id: 'scotty', 
    label: 'Scotty', 
    desc: 'AI-powered guest concierge. Handles FAQs and lead capture 24/7 with zero human oversight.', 
    icon: FiIcons.FiCpu, 
    color: 'violet', 
    radiusX: 364, 
    radiusY: 208, 
    angle: 102.8, 
    type: 'product' 
  },
  { 
    id: 'memories', 
    label: 'Memories', 
    desc: 'The private social network for your events. Capture and share every moment with AI-powered galleries.', 
    icon: FiIcons.FiImage, 
    color: 'purple', 
    radiusX: 364, 
    radiusY: 208, 
    angle: 154.2, 
    type: 'product' 
  },
  { 
    id: 'avatars', 
    label: 'Avatars', 
    desc: 'Immersive virtual venues. Host high-capacity expos and networking events at 85% lower costs.', 
    icon: FiIcons.FiUsers, 
    color: 'blue', 
    radiusX: 364, 
    radiusY: 208, 
    angle: 205.6, 
    type: 'product' 
  },
  { 
    id: 'bifrost', 
    label: 'Bifrost', 
    desc: 'The social bridge. Publish content to 13 networks simultaneously through a single email draft.', 
    icon: FiIcons.FiZap, 
    color: 'pink', 
    radiusX: 364, 
    radiusY: 208, 
    angle: 257, 
    type: 'product' 
  },
  { 
    id: 'nima', 
    label: 'Nima', 
    desc: 'Automation engine. Connect your event data to 1,000+ apps like HubSpot, Slack, and Salesforce.', 
    icon: FiIcons.FiActivity, 
    color: 'cyan', 
    radiusX: 364, 
    radiusY: 208, 
    angle: 308.4, 
    type: 'product' 
  },
  // Features (Outer Oval Ring - Distances increased by 30%)
  { 
    id: 'commerce', 
    label: 'E-commerce', 
    desc: 'Scalable ticket sales with 6 integrated payment providers.', 
    icon: FiIcons.FiShoppingCart, 
    color: 'slate', 
    radiusX: 546, // 420 * 1.3
    radiusY: 338, // 260 * 1.3
    angle: 30, 
    type: 'feature' 
  },
  { 
    id: 'whitelabel', 
    label: 'Whitelabel', 
    desc: 'Host on your own domain with full brand control and zero BookMeIn credit.', 
    icon: FiIcons.FiShield, 
    color: 'slate', 
    radiusX: 546, 
    radiusY: 338, 
    angle: 90, 
    type: 'feature' 
  },
  { 
    id: 'api', 
    label: 'REST API', 
    desc: 'Enterprise-grade endpoints for custom mobile apps and deep CRM integrations.', 
    icon: FiIcons.FiLink, 
    color: 'slate', 
    radiusX: 546, 
    radiusY: 338, 
    angle: 150, 
    type: 'feature' 
  },
  { 
    id: 'payments', 
    label: 'Payments', 
    desc: 'Multi-currency support (135+) with instant localized pricing for guests.', 
    icon: FiIcons.FiCreditCard, 
    color: 'slate', 
    radiusX: 546, 
    radiusY: 338, 
    angle: 210, 
    type: 'feature' 
  },
  { 
    id: 'feedback', 
    label: 'Feedback', 
    desc: 'Real-time sentiment analysis and post-event survey automation.', 
    icon: FiIcons.FiMessageSquare, 
    color: 'slate', 
    radiusX: 546, 
    radiusY: 338, 
    angle: 270, 
    type: 'feature' 
  },
  { 
    id: 'marketplace', 
    label: 'Marketplace', 
    desc: 'A private, embeddable hub for your entire seasonal event lineup.', 
    icon: FiIcons.FiGlobe, 
    color: 'slate', 
    radiusX: 546, 
    radiusY: 338, 
    angle: 330, 
    type: 'feature' 
  },
];

const EcosystemMindMap = () => {
  const [activeNode, setActiveNode] = useState(null);

  const getCoordinates = (rx, ry, angle) => {
    const radian = (angle - 90) * (Math.PI / 180);
    return {
      x: rx * Math.cos(radian),
      y: ry * Math.sin(radian)
    };
  };

  return (
    <div className="relative w-full aspect-[16/11] max-w-[1200px] mx-auto flex items-center justify-center overflow-visible py-32">
      {/* Background Oval Rings - Scaled to match new radii */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="absolute w-[728px] h-[416px] border border-indigo-100 rounded-[100%] animate-[pulse_8s_ease-in-out_infinite]" />
        <div className="absolute w-[1092px] h-[676px] border border-dashed border-slate-100 rounded-[100%] opacity-50" />
      </div>

      {/* Central Hub */}
      <motion.div 
        whileHover={{ scale: 1.05 }}
        className="relative z-30 group cursor-pointer"
      >
        <motion.div
          animate={{ 
            boxShadow: [
              "0 0 20px rgba(79, 70, 229, 0.2)",
              "0 0 80px rgba(79, 70, 229, 0.4)",
              "0 0 20px rgba(79, 70, 229, 0.2)"
            ] 
          }}
          transition={{ duration: 4, repeat: Infinity }}
          className="w-60 h-60 bg-slate-900 rounded-full flex flex-col items-center justify-center border-[8px] border-white shadow-2xl relative overflow-hidden"
        >
          {/* Concentrated Core Gradient */}
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            className="absolute inset-[25%] bg-[conic-gradient(from_0deg,#4f46e5,#3b82f6,#8b5cf6,#4f46e5)] opacity-60 blur-2xl"
          />
          
          <div className="relative z-10 text-center flex flex-col items-center">
            <div className="mb-4 p-4 bg-white rounded-xl shadow-xl transform rotate-3 transition-transform group-hover:rotate-0">
              <SafeIcon icon={FiIcons.FiCommand} className="w-10 h-10 text-indigo-600" />
            </div>
            <div className="flex flex-col items-center">
              <span className="text-[10px] font-black uppercase tracking-[0.5em] text-indigo-300 ml-1 mb-1">BookMeIn</span>
              <div className="h-px w-12 bg-white/20 mb-1" />
              <span className="text-2xl font-black text-white tracking-tighter leading-none italic">EVENTS</span>
            </div>
          </div>
          <div className="absolute inset-3 border border-white/10 rounded-full pointer-events-none" />
        </motion.div>
      </motion.div>

      {/* Connection Lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#818cf8" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        {ecosystemData.map((node) => {
          const { x, y } = getCoordinates(node.radiusX, node.radiusY, node.angle);
          const isTargeted = activeNode === node.id;
          return (
            <motion.line
              key={`line-${node.id}`}
              x1="50%"
              y1="50%"
              x2={`calc(50% + ${x}px)`}
              y2={`calc(50% + ${y}px)`}
              stroke={node.type === 'product' ? 'url(#lineGrad)' : '#e2e8f0'}
              strokeWidth={isTargeted ? "3" : (node.type === 'product' ? "2" : "1")}
              strokeDasharray={node.type === 'feature' ? "6 4" : "0"}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: isTargeted ? 1 : (activeNode ? 0.1 : 0.4) }}
              transition={{ duration: 1.2, type: 'spring' }}
            />
          );
        })}
      </svg>

      {/* Floating Nodes */}
      {ecosystemData.map((node, i) => {
        const { x, y } = getCoordinates(node.radiusX, node.radiusY, node.angle);
        const isActive = activeNode === node.id;
        return (
          <motion.div
            key={node.id}
            className={`absolute ${isActive ? 'z-50' : 'z-20'}`}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: isActive ? 1 : (activeNode ? 0.4 : 1), 
              scale: isActive ? 1.05 : 1, 
              x, 
              y,
              transition: { delay: i * 0.05, type: 'spring', stiffness: 80 } 
            }}
            onMouseEnter={() => setActiveNode(node.id)}
            onMouseLeave={() => setActiveNode(null)}
          >
            <motion.div
              animate={{ 
                y: isActive ? -15 : [0, -12, 0],
                rotate: isActive ? 0 : [0, 1.5, 0, -1.5, 0]
              }}
              transition={{ duration: 5 + (i % 3), repeat: Infinity, ease: "easeInOut" }}
              className="relative group cursor-pointer"
            >
              <div className={`
                flex items-center gap-3 p-4 rounded-[2rem] border transition-all duration-500
                ${node.type === 'product' 
                  ? 'bg-white shadow-2xl border-slate-100 group-hover:border-indigo-400' 
                  : 'bg-slate-50/90 backdrop-blur-sm border-transparent group-hover:bg-white group-hover:border-slate-200 group-hover:shadow-xl'}
                ${isActive ? 'ring-[8px] ring-indigo-500/20 scale-110 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)]' : ''}
              `}>
                <div className={`
                  p-3 rounded-2xl transition-all duration-700 shadow-lg
                  ${node.type === 'product' 
                    ? `bg-${node.color}-50 text-${node.color}-600` 
                    : 'bg-slate-200 text-slate-500'}
                  group-hover:bg-indigo-600 group-hover:text-white group-hover:rotate-[360deg]
                  ${isActive ? 'bg-indigo-600 text-white' : ''}
                `}>
                  <SafeIcon icon={node.icon} className="w-5 h-5" />
                </div>
                <div className="pr-2">
                  <p className={`text-[12px] font-black uppercase tracking-[0.15em] ${node.type === 'product' ? 'text-slate-900' : 'text-slate-500'}`}>
                    {node.label}
                  </p>
                </div>
              </div>

              <AnimatePresence>
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 15 }}
                    className="absolute top-full mt-10 left-1/2 -translate-x-1/2 w-80 bg-black/95 backdrop-blur-2xl text-white p-7 rounded-[2.5rem] shadow-[0_40px_80px_-15px_rgba(0,0,0,0.8)] z-[60] border border-white/20"
                  >
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-5 h-5 bg-black rotate-45 border-l border-t border-white/20" />
                    <div className="flex items-center gap-4 mb-4 border-b border-white/10 pb-4">
                      <div className="p-2.5 bg-indigo-500/30 rounded-xl border border-white/10">
                        <SafeIcon icon={node.icon} className="w-5 h-5 text-indigo-300" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] font-black uppercase tracking-[0.25em] text-indigo-300">{node.label}</span>
                        <span className="text-[9px] uppercase font-bold text-slate-500 tracking-widest">{node.type} module</span>
                      </div>
                    </div>
                    <p className="text-[14px] leading-relaxed font-medium text-slate-200 antialiased">{node.desc}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default EcosystemMindMap;